import type { Vector3 } from "./types";

export function add(first: Vector3, second: Vector3): Vector3 {
  return [first[0] + second[0], first[1] + second[1], first[2] + second[2]];
}

export function scale(vector: Vector3, factor: number): Vector3 {
  return [vector[0] * factor, vector[1] * factor, vector[2] * factor];
}

export function dot(first: Vector3, second: Vector3): number {
  return first[0] * second[0] + first[1] * second[1] + first[2] * second[2];
}

export function cross(first: Vector3, second: Vector3): Vector3 {
  return [
    first[1] * second[2] - first[2] * second[1],
    first[2] * second[0] - first[0] * second[2],
    first[0] * second[1] - first[1] * second[0],
  ];
}

export function magnitude(vector: Vector3): number {
  return Math.sqrt(dot(vector, vector));
}

export function normalize(vector: Vector3): Vector3 {
  const length = magnitude(vector);
  if (length === 0) {
    throw new RangeError("Cannot normalize a zero-length vector");
  }
  return scale(vector, 1 / length);
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

export function angleBetweenDegrees(first: Vector3, second: Vector3): number {
  const cosine = clamp(dot(normalize(first), normalize(second)), -1, 1);
  if (cosine >= 1 - Number.EPSILON) {
    return 0;
  }
  if (cosine <= -1 + Number.EPSILON) {
    return 180;
  }
  return radiansToDegrees(Math.acos(cosine));
}

export function rotateAroundAxis(
  vector: Vector3,
  axis: Vector3,
  angle: number,
): Vector3 {
  const unitAxis = normalize(axis);
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  const parallel = scale(unitAxis, dot(unitAxis, vector) * (1 - cosine));
  return add(
    add(scale(vector, cosine), scale(cross(unitAxis, vector), sine)),
    parallel,
  );
}
